// Import Libraries
import React, { useState, useEffect, useRef, useContext } from 'react';
import { ButtonBase, InputBase, Tooltip, Typography } from '@mui/material';
import { toast } from 'react-toastify';

// Context
import ScriptContext from '../../../context/Script';

// Import Services
import ScriptService from '../../../services/scriptService';

// Construct Component
// Project title: an h6 that truncates with an ellipsis (full title in the
// tooltip) and, for owners/editors, turns into an inline input on click.
const ProjectTitle = () => {
  const { script, setScript } = useContext(ScriptContext);
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const name = script?.script_name || '';
  const canEdit = script?.level === 'owner' || script?.level === 'editor';

  useEffect(() => {
    setValue(name);
  }, [name]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const cancel = () => {
    setValue(name);
    setIsEditing(false);
  };

  const save = async () => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === name) {
      cancel();
      return;
    }
    setSaving(true);
    try {
      await ScriptService.updateScriptName(script.id, trimmed);
      setScript((prev) => ({ ...prev, script_name: trimmed }));
      toast.success('Project title has been saved', { autoClose: 1000, hideProgressBar: true });
    } catch (e) {
      setValue(name);
      toast.error('Failed to save change. Please try again.');
    }
    setSaving(false);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') save();
    if (e.key === 'Escape') cancel();
  };

  if (isEditing) {
    return (
      <InputBase
        inputRef={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={handleKeyDown}
        disabled={saving}
        inputProps={{ maxLength: 200, 'aria-label': 'Project title' }}
        sx={{ flex: '1 1 auto', minWidth: 0 }}
      />
    );
  }

  // A heading can't live inside a <button>, so the editable title is a span.
  const title = (
    <Typography variant="h6" component={canEdit ? 'span' : 'h1'} noWrap>
      {name}
    </Typography>
  );

  return (
    <Tooltip title={canEdit ? `${name} · Click to rename` : name}>
      {canEdit ? (
        <ButtonBase
          onClick={() => setIsEditing(true)}
          aria-label={`Rename project: ${name}`}
          sx={{ minWidth: 0, flex: '0 1 auto', justifyContent: 'flex-start', borderRadius: 1 }}
        >
          {title}
        </ButtonBase>
      ) : (
        <span style={{ minWidth: 0, flex: '0 1 auto', display: 'flex' }}>{title}</span>
      )}
    </Tooltip>
  );
};

export default ProjectTitle;
